import { useEffect, useRef, useState } from 'react'
import Modal from './Modal'
import Boton from './Boton'
import { pagosProveedorApi, CANALES_LEGAJO } from '../api/pagosProveedor'
import { fmtFecha } from '../utils/fecha'
import { colors, font, text, space, radius } from '../design/tokens'

/**
 * Envío del legajo de un pago a proveedor por uno de los tres canales
 * (Martin, 26 oct 2026). El email y el teléfono vienen precargados con el
 * último usado o el de Presea; si el pagador los cambia, quedan para el
 * próximo pago de ese proveedor.
 */
export default function ModalEnviarLegajo({ pagoId, onClose, onEnviado }) {
  const [det, setDet] = useState(null)
  const [error, setError] = useState(null)
  const [canal, setCanal] = useState('email_lgr')
  const [email, setEmail] = useState('')
  const [telefono, setTelefono] = useState('')
  const [mensaje, setMensaje] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [waLink, setWaLink] = useState(null)
  const [mailto, setMailto] = useState(null)
  const [archivos, setArchivos] = useState([])
  const [conCopia, setConCopia] = useState(true)
  const [arrastrando, setArrastrando] = useState(false)
  const [rechazados, setRechazados] = useState([])
  const inputRef = useRef(null)

  useEffect(() => {
    pagosProveedorApi.ver(pagoId).then(({ data }) => {
      setDet(data)
      setEmail(data.contacto?.email || '')
      setTelefono(data.contacto?.telefono || '')
      if (!data.contacto?.email && data.contacto?.telefono) setCanal('whatsapp')
    }).catch(() => setError('No se pudo cargar el pago.'))
  }, [pagoId])

  const origen = (o) => o === 'ultimo'
    ? `último usado${det?.contacto?.cambiado_por ? ` · ${det.contacto.cambiado_por}` : ''}${det?.contacto?.cambiado_at ? ` · ${fmtFecha(det.contacto.cambiado_at)}` : ''}`
    : o === 'presea' ? 'de Presea' : 'sin dato: escribilo'

  const enviar = async () => {
    setEnviando(true); setError(null)
    try {
      const { data } = await pagosProveedorApi.enviar(pagoId, {
        canal,
        email: canal === 'whatsapp' ? null : email,
        telefono: canal === 'whatsapp' ? telefono : null,
        mensaje: mensaje || null,
        con_copia: conCopia ? '1' : '0',
      }, canal === 'email_local' ? [] : archivos)
      if (canal === 'whatsapp') { setWaLink(data.wa_link); onEnviado?.(data, { cerrar: false }) }
      else if (canal === 'email_local') {
        // El correo de la computadora: se baja el legajo y se abre el programa
        // de correo con el mensaje armado; el PDF lo adjunta la persona.
        setMailto(data.mailto)
        await pagosProveedorApi.descargarPdf(pagoId, `Legajo-OP-${det.numero}.pdf`)
        window.location.href = data.mailto
        onEnviado?.(data, { cerrar: false })
      } else onEnviado?.(data, { cerrar: true })
    } catch (e) {
      const errs = e.response?.data?.errors
      setError(errs ? Object.values(errs).flat()[0] : (e.response?.data?.message || 'No se pudo enviar.'))
    } finally { setEnviando(false) }
  }

  // Los archivos se copian ANTES de vaciar el selector: e.target.files es una lista
  // viva y vaciar el input la deja en cero (así no se adjuntaba nada — 28 oct 2026).
  // También se pueden soltar arrastrándolos (Martin, 28 oct 2026); lo que no es de
  // un tipo que el servidor acepta se avisa acá en vez de fallar al enviar.
  const sumarArchivos = (lista) => {
    const todos = Array.from(lista || [])
    const ok = todos.filter(f => TIPOS.includes((f.name.split('.').pop() || '').toLowerCase()))
    setRechazados(todos.filter(f => !ok.includes(f)).map(f => f.name))
    setArchivos(a => [...a, ...ok])
  }
  const pesoMb = archivos.reduce((t, f) => t + f.size, 0) / 1048576

  const esMail = canal !== 'whatsapp'

  return (
    <Modal title={det ? `Enviar legajo · OP ${det.numero}` : 'Enviar legajo'} onClose={onClose} maxWidth="560px">
      {!det && !error && <p style={st.meta}>Cargando…</p>}
      {det && det.legajo_error && <div style={st.err}>{det.legajo_error}</div>}
      {det && det.legajo && (
        <div style={{ display: 'grid', gap: space.lg }}>
          <div style={st.resumen}>
            <div style={st.prov}>{det.legajo.proveedor.nombre}</div>
            <div style={st.meta}>
              <span style={st.mono}>$ {det.legajo.pago.total.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
              {' · '}{det.legajo.retenciones.length} constancia{det.legajo.retenciones.length === 1 ? '' : 's'}
              {det.legajo.transferencias_mp.length > 0 && ' · comprobante MP'}
            </div>
          </div>

          <div role="radiogroup" aria-label="Canal" style={st.canales}>
            {CANALES_LEGAJO.map(c => (
              <label key={c.value} style={{ ...st.canal, ...(canal === c.value ? st.canalOn : {}) }}>
                <input type="radio" name="canal" value={c.value} checked={canal === c.value} onChange={() => { setCanal(c.value); setWaLink(null); setMailto(null) }} />
                <span><b style={{ fontWeight: 600 }}>{c.label}</b><br /><span style={st.meta}>{c.ayuda}</span></span>
              </label>
            ))}
          </div>

          {esMail ? (
            <label style={st.campo}>
              <span style={st.lbl}>Email del proveedor <span style={st.origen}>({origen(email === (det.contacto?.email || '') ? det.contacto?.email_origen : 'nuevo')})</span></span>
              <input id="legajo-email" style={st.input} value={email} onChange={e => setEmail(e.target.value)} placeholder="proveedor@empresa.com (varios separados por coma)" />
            </label>
          ) : (
            <label style={st.campo}>
              <span style={st.lbl}>WhatsApp del proveedor <span style={st.origen}>({origen(telefono === (det.contacto?.telefono || '') ? det.contacto?.telefono_origen : 'nuevo')})</span></span>
              <input id="legajo-telefono" style={st.input} value={telefono} onChange={e => setTelefono(e.target.value)} placeholder="54 9 11 1234-5678" />
            </label>
          )}

          {esMail && det.copia?.length > 0 && (
            <label style={st.check}>
              <input id="legajo-copia" type="checkbox" checked={conCopia} onChange={e => setConCopia(e.target.checked)} />
              <span>Con copia a <b style={{ fontWeight: 600 }}>{det.copia.join(', ')}</b></span>
            </label>
          )}

          <label style={st.campo}>
            <span style={st.lbl}>Mensaje (opcional)</span>
            <textarea id="legajo-mensaje" style={{ ...st.input, minHeight: 64, fontFamily: 'inherit' }} value={mensaje} onChange={e => setMensaje(e.target.value)}
              placeholder={esMail ? 'Se agrega arriba del texto del mail' : det.texto_whatsapp} />
          </label>

          {canal === 'email_local' ? (
            <div style={st.meta}>Los archivos que quieras sumar los adjuntás vos en tu programa de correo, junto con el legajo que se descarga.</div>
          ) : (
            <div style={st.campo}>
              <span style={st.lbl}>Adjuntos (opcional) <span style={st.origen}>— comprobantes del banco o de Mercado Pago, u otros; los que quieras, hasta 8 MB en total</span></span>
              <div role="button" tabIndex={0} aria-label="Soltá los archivos acá o hacé clic para elegirlos"
                style={{ ...st.zona, ...(arrastrando ? st.zonaOn : {}) }}
                onClick={() => inputRef.current?.click()}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inputRef.current?.click() } }}
                onDragOver={e => { e.preventDefault(); setArrastrando(true) }}
                onDragLeave={() => setArrastrando(false)}
                onDrop={e => { e.preventDefault(); setArrastrando(false); sumarArchivos(e.dataTransfer.files) }}>
                {arrastrando ? 'Soltá los archivos' : <>Arrastrá los archivos acá o <span style={st.zonaLink}>elegilos</span></>}
              </div>
              <input id="legajo-adjuntos" ref={inputRef} type="file" multiple hidden accept={TIPOS.map(t => '.' + t).join(',')}
                onChange={e => { sumarArchivos(e.target.files); e.target.value = '' }} />
              {rechazados.length > 0 && <div style={st.err}>No se pueden adjuntar: {rechazados.join(', ')} (solo PDF, imágenes, Excel, Word, TXT o CSV).</div>}
              {archivos.length > 0 && (
                <div style={st.adjuntos}>
                  {archivos.map((f, i) => (
                    <span key={i} style={st.adjunto}>📎 {f.name}
                      <button type="button" style={st.quitar} aria-label={`Quitar ${f.name}`} onClick={() => setArchivos(a => a.filter((_, j) => j !== i))}>✕</button>
                    </span>
                  ))}
                  <span style={{ ...st.meta, color: pesoMb > 8 ? colors.err : colors.textSec }}>{pesoMb.toFixed(1)} MB</span>
                </div>
              )}
            </div>
          )}

          {error && <div style={st.err}>{error}</div>}

          {mailto ? (
            <div style={st.ok}>
              Se descargó el legajo y se abrió tu programa de correo. Adjuntá el PDF y enviá.
              <div style={{ display: 'flex', gap: space.md, marginTop: 6 }}>
                <a href={mailto} style={{ color: colors.link, fontWeight: 600 }}>Abrir el correo de nuevo</a>
                <button type="button" style={st.linkBtn} onClick={() => pagosProveedorApi.descargarPdf(pagoId, `Legajo-OP-${det.numero}.pdf`)}>Bajar el PDF otra vez</button>
              </div>
              <div style={st.meta}>Queda registrado como preparado en tu correo: el envío lo hacés vos.</div>
            </div>
          ) : waLink ? (
            <div style={st.ok}>
              Legajo listo. <a href={waLink} target="_blank" rel="noreferrer" style={{ color: colors.link, fontWeight: 600 }}>Abrir WhatsApp ↗</a>
              <div style={st.meta}>Queda registrado como abierto en WhatsApp: el envío lo confirmás vos en la conversación.</div>
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: space.md }}>
              <Boton variante="ghost" onClick={onClose}>Cancelar</Boton>
              <Boton variante="send" disabled={enviando || pesoMb > 8 || (esMail ? !email.trim() : !telefono.trim())} onClick={enviar}>
                {enviando ? 'Preparando…' : canal === 'email_lgr' ? '✉ Enviar legajo' : canal === 'email_local' ? '✉ Abrir en mi correo' : '→ Preparar WhatsApp'}
              </Boton>
            </div>
          )}
        </div>
      )}
      {error && !det && <div style={st.err}>{error}</div>}
    </Modal>
  )
}

// Los tipos que acepta el servidor (PagoProveedorController::enviar).
const TIPOS = ['pdf', 'jpg', 'jpeg', 'png', 'webp', 'xls', 'xlsx', 'doc', 'docx', 'txt', 'csv']

const st = {
  zona: { marginTop: 4, padding: '14px 12px', border: `1px dashed ${colors.textPlh}`, borderRadius: radius.sm, textAlign: 'center', fontSize: text.sec, color: colors.textSec, background: colors.bgSoft, cursor: 'pointer' },
  zonaOn: { borderColor: colors.linkBorder, background: colors.linkBg, color: colors.link },
  zonaLink: { color: colors.link, fontWeight: 600 },
  resumen: { padding: space.md, background: colors.bgSoft, border: `1px solid ${colors.border}`, borderRadius: radius.md },
  prov: { fontSize: text.body, fontWeight: 600, color: colors.text },
  meta: { fontSize: text.sec, color: colors.textSec },
  mono: { fontFamily: font.mono, fontVariantNumeric: 'tabular-nums' },
  canales: { display: 'grid', gap: space.sm },
  canal: { display: 'flex', gap: space.md, alignItems: 'flex-start', padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, cursor: 'pointer', fontSize: text.body },
  canalOn: { borderColor: colors.linkBorder, background: colors.linkBg },
  campo: { display: 'grid', gap: 4 },
  check: { display: 'flex', alignItems: 'center', gap: space.sm, fontSize: text.body, color: colors.text, cursor: 'pointer', marginTop: -6 },
  lbl: { fontSize: text.sec, fontWeight: 600, color: colors.textSub },
  origen: { fontWeight: 400, color: colors.textMeta },
  input: { padding: '6px 10px', border: `1px solid ${colors.textPlh}`, borderRadius: radius.sm, fontSize: text.body, background: colors.bg, color: colors.text },
  err: { padding: '8px 12px', background: colors.errBg, color: colors.err, borderRadius: radius.sm, fontSize: text.sec },
  adjuntos: { display: 'flex', flexWrap: 'wrap', gap: space.sm, alignItems: 'center', marginTop: 4 },
  adjunto: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: text.sec, background: colors.bgSoft },
  quitar: { border: 'none', background: 'none', cursor: 'pointer', color: colors.textSec, padding: 0 },
  linkBtn: { border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: colors.link, fontWeight: 600, fontSize: text.body },
  ok: { padding: '10px 12px', background: colors.okSoft, border: `1px solid ${colors.okBg}`, borderRadius: radius.sm, fontSize: text.body },
}
